import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderNextNavButton,
  HeaderNav,
  HeaderSidebarTrigger,
  HeaderPrevNavButton,
  useHeaderNav,
} from '@/app/ui/components/header';
import { EvaluationTypeLabelsEnum, AmbiguityTypes, queryKeys } from '@/app/utils/constants';
import { AuctionIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { MainScrollArea } from '@/app/ui/components/main';
import { Spinner } from '@/app/ui/components/spinner';
import { useGetAmbiguities } from '@/app/components/features/evaluations/analysis/Ambiguities/useAmbiguity';
import { AmbiguityDetail } from '@/app/components/features/evaluations/analysis/Ambiguities/AmbiguityDetail';
import { getBidDetailsQuery } from '@/app/hooks/queries/useBids';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSuspenseQuery } from '@tanstack/react-query';
import { NotFound } from '@/app/ui/components/not-found';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/$bidId/_layout/ambiguities/$ambiguityId',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const tFeature = useTranslations('features.ambiguities');
  const tBid = useTranslations('pages.bids');
  const { bidId, tenderPackageId, projectId, ambiguityId } = Route.useParams();
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: bid } = useSuspenseQuery(getBidDetailsQuery(bidId));

  const { data: allAmbiguitiesData, isLoading } = useGetAmbiguities({
    ambiguityType: AmbiguityTypes.BID,
    id: bidId,
  });
  const allStatements = allAmbiguitiesData?.statements;

  const data = useMemo(
    () => allStatements?.find((item) => item.id === ambiguityId),
    [allStatements, ambiguityId],
  );

  const { handlePrevious, handleNext, isPreviousDisabled, isNextDisabled, currentIndex } =
    useHeaderNav(allStatements || [], ambiguityId, (ambiguityId) => ({
      to: '/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/ambiguities/$ambiguityId',
      params: { projectId, tenderPackageId, bidId, ambiguityId },
    }));

  if (!data && !isLoading) return <NotFound />;

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/ambiguities"
          params={{ projectId, tenderPackageId, bidId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId/bids"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={AuctionIcon} />
                {tBid('title')}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                {evaluation.name}
              </BreadcrumbMenuItem>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId"
                params={{ projectId, tenderPackageId, bidId }}
              >
                <span className="line-clamp-1">{bid.bidAdditionalName || bid.companyName}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId/bids/$bidId/ambiguities"
                params={{ projectId, tenderPackageId, bidId }}
              >
                {tFeature('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="tabular-nums">#{currentIndex + 1}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderNav>
          <HeaderPrevNavButton onClick={handlePrevious} disabled={isPreviousDisabled} />
          <HeaderNextNavButton onClick={handleNext} disabled={isNextDisabled} />
        </HeaderNav>
        <HeaderSidechatButton />
      </Header>
      <MainScrollArea>
        {isLoading ? (
          <Spinner kind="inset" />
        ) : data ? (
          <AmbiguityDetail
            data={data}
            currentIndex={currentIndex}
            ambiguityType={AmbiguityTypes.BID}
            evaluationId={bidId}
            queryKeysToInvalidate={[[queryKeys.AMBIGUITY_STATEMENTS, AmbiguityTypes.BID, bidId]]}
          />
        ) : null}
      </MainScrollArea>
    </>
  );
}
